/**
 * Amazon affiliate URL tagging (ported from yoto-web ecom.ts / redirect worker).
 */

import type { AdAccountConfig } from '../../core/config/types';

export const AMAZON_BUCKET_MODE_TAGS = 'tags';
export const AMAZON_BUCKET_MODE_SUBTAGS = 'subtags';
export const AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS = 'dynamic_subtags';

export function getAmazonBucketMode(mode?: string): string {
  if (mode === AMAZON_BUCKET_MODE_SUBTAGS || mode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS) {
    return mode;
  }
  return AMAZON_BUCKET_MODE_TAGS;
}

/**
 * Redirect `getPrefixedChannel` — keep QS/`ch` only when it matches `tagsPrefix`.
 * Empty prefix → reject (fall back to default tag).
 */
export function getPrefixedChannel(
  channel: string | null | undefined,
  tagsPrefix: string | null | undefined,
): string | null {
  if (!channel) return null;
  if (!tagsPrefix) return null;
  if (channel.includes('{')) return null;
  return channel.startsWith(tagsPrefix) ? channel : null;
}

/** Allow channel when prefix check is off; still reject `{…}` placeholders. */
export function allowChannelWithoutPrefix(
  channel: string | null | undefined,
): string | null {
  if (!channel) return null;
  if (channel.includes('{')) return null;
  return channel;
}

function getDynamicAmazonSubtag(mbid: string): string {
  const firstPart = (mbid || '').split('_')[0];
  if (!firstPart || firstPart === '0' || firstPart.indexOf('{') > -1) {
    return 'default';
  }
  return firstPart;
}

export interface AmazonUrlOptions {
  defaultTag: string;
  channel?: string | null;
  mbid?: string;
  adAccount?: AdAccountConfig | null;
  sessionTag?: string | null;
  useSessionAttribution?: boolean;
  runtimeAdTagAssignment?: RuntimeAdTagAssignment | null;
  /**
   * Config `ecom_tags_prefix`. When `enforceTagsPrefix` is true (default), QS
   * channel must match this prefix.
   */
  tagsPrefix?: string | null;
  /**
   * When false (`ecom.init({ enforceTagsPrefix: false })`), skip prefix check
   * (yoto `ecmtrtg_man` parity). Default true.
   */
  enforceTagsPrefix?: boolean;
}

export interface RuntimeAdTagAssignment {
  tag: string | null;
  parentTag?: string;
  mode?: string;
  assignmentId?: string;
  fallback: boolean;
  fallbackReason?: string;
  fallbackStatus?: number;
}

export interface AmazonUrlResult {
  url: string;
  /** Subid or tag written to the link; maps to `/dye` `ecsid`. */
  clickId: string;
  /** Parent associate tag in subtag mode; maps to `/dye` `ecptg`. */
  ecptg?: string;
}

export function buildAmazonUrl(rawUrl: string, opts: AmazonUrlOptions): AmazonUrlResult {
  const url = new URL(rawUrl);
  const adAccount = opts.adAccount;
  const adAssignmentFallback = Boolean(opts.runtimeAdTagAssignment?.fallback);
  const adBucketMode = getAmazonBucketMode(
    adAssignmentFallback ? AMAZON_BUCKET_MODE_TAGS : opts.runtimeAdTagAssignment?.tag ? opts.runtimeAdTagAssignment.mode : adAccount?.adTagBucketMode,
  );
  const sessionBucketMode = getAmazonBucketMode(adAccount?.sessionTagBucketMode);
  const mbid = opts.mbid ?? '';
  const enforceTagsPrefix = opts.enforceTagsPrefix !== false;

  const sessionAttributionReplaced = Boolean(
    !opts.runtimeAdTagAssignment && opts.useSessionAttribution && opts.sessionTag,
  );
  const usesSessionSubtagMode =
    sessionAttributionReplaced &&
    (sessionBucketMode === AMAZON_BUCKET_MODE_SUBTAGS ||
      sessionBucketMode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS);
  const usesAdSubtagMode =
    !sessionAttributionReplaced &&
    (adBucketMode === AMAZON_BUCKET_MODE_SUBTAGS || adBucketMode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS);
  const usesDynamicSubtag =
    (sessionAttributionReplaced && sessionBucketMode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS) ||
    (!sessionAttributionReplaced && adBucketMode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS);
  const usesAdChannel =
    !sessionAttributionReplaced &&
    (adBucketMode === AMAZON_BUCKET_MODE_TAGS || adBucketMode === AMAZON_BUCKET_MODE_SUBTAGS);

  let channel: string | null = null;
  if (adAssignmentFallback) {
    channel = null;
  } else if (opts.runtimeAdTagAssignment?.tag) {
    channel = opts.runtimeAdTagAssignment.tag;
  } else if (sessionAttributionReplaced) {
    channel = opts.sessionTag ?? null;
  } else if (usesAdChannel) {
    channel = enforceTagsPrefix
      ? getPrefixedChannel(opts.channel, opts.tagsPrefix)
      : allowChannelWithoutPrefix(opts.channel);
  }

  if (!channel || usesDynamicSubtag) {
    channel = null;
  }

  if (usesSessionSubtagMode) {
    return setAmazonTrackingParams(
      url,
      sessionBucketMode,
      adAccount?.sessionTagParentTag,
      channel,
      opts.defaultTag,
      mbid,
    );
  }
  if (usesAdSubtagMode) {
    return setAmazonTrackingParams(
      url,
      adBucketMode,
      opts.runtimeAdTagAssignment?.parentTag || adAccount?.adTagParentTag,
      channel,
      opts.defaultTag,
      mbid,
    );
  }

  const tag = channel || opts.defaultTag;
  url.searchParams.set('tag', tag);
  return { url: url.toString(), clickId: tag };
}

function setAmazonTrackingParams(
  url: URL,
  mode: string,
  parentTag: string | undefined,
  channel: string | null,
  fallbackTag: string,
  mbid: string,
): AmazonUrlResult {
  if (mode === AMAZON_BUCKET_MODE_SUBTAGS || mode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS) {
    const subtag =
      mode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS ? getDynamicAmazonSubtag(mbid) : channel || 'default';
    const parent = parentTag || fallbackTag;
    url.searchParams.set('tag', parent);
    url.searchParams.set('ascsubtag', subtag);
    return {
      url: url.toString(),
      clickId: subtag,
      ...(parentTag ? { ecptg: parentTag } : {}),
    };
  }
  const tag = channel || fallbackTag;
  url.searchParams.set('tag', tag);
  return { url: url.toString(), clickId: tag };
}
